import { create } from "zustand";
import { fetchTrafficSummary as apiFetchTrafficSummary } from "./trackingApi";

/**
 * Server-aggregated traffic summary, keyed by the exact query that produced it.
 *
 * Keyed by (brand, days, granularity) rather than by brand alone. The raw-event store this
 * replaced recorded only WHICH brand was loaded, so two surfaces asking for different windows
 * silently showed each other's answer — the dashboard would report /web-traffic's 7-day slice as
 * a 30-day summary, and a filter set on one page changed what the other displayed.
 */
export const useTrafficSummaryStore = create((set, get) => ({
  summary: null,
  isLoading: false,
  /** The query the current summary answers, so a consumer can tell whether it is theirs. */
  loadedKey: null,
  /** The query that failed, so a consumer can distinguish "still loading" from "this broke". */
  failedKey: null,
  error: null,

  /**
   * Which request is current. Responses can arrive out of order — switch period twice quickly and
   * the first response may land last — so a stale one must not overwrite a newer answer, and must
   * not clear the loading flag that belongs to the newer request.
   */
  inFlightKey: null,

  fetchSummary: async (projectId, { days = 30, granularity = "day" } = {}) => {
    if (!projectId) return null;
    const key = `${projectId}|${days}|${granularity}`;

    set({ isLoading: true, inFlightKey: key, error: null, failedKey: null });

    try {
      const summary = await apiFetchTrafficSummary(projectId, { days, granularity });
      if (get().inFlightKey !== key) return summary; // superseded; leave the newer request alone
      set({ summary, loadedKey: key, isLoading: false });
      return summary;
    } catch (err) {
      console.error("Failed to fetch traffic summary", err);
      if (get().inFlightKey !== key) return null;
      // Record WHICH query failed. Clearing loadedKey and leaving it at that made every consumer
      // read "not loaded yet" forever, so a failed request showed a permanent "Loading..." with
      // no error anywhere on the page.
      set({
        isLoading: false,
        summary: null,
        loadedKey: null,
        failedKey: key,
        error: err?.message ?? "Could not load traffic data.",
      });
      return null;
    }
  },

  /** True when the store holds the answer to exactly this question. */
  hasSummaryFor: (projectId, { days = 30, granularity = "day" } = {}) =>
    get().loadedKey === `${projectId}|${days}|${granularity}`,

  /** True when this exact question was asked and failed. */
  hasFailedFor: (projectId, { days = 30, granularity = "day" } = {}) =>
    get().failedKey === `${projectId}|${days}|${granularity}`,
}));
